/**
 * Acquisition-Pathway parcat — Vega-Lite port of the notebook chart at
 * `worktrees/govdata-eda/notebooks/acquisition_pathway_explorer.py` lines
 * 1022–1454.
 *
 * Spec structure (mirrors the notebook):
 *   1. Bar-bounds per (axis, category): stacked height ∝ award_count
 *   2. Per-path y-slots per axis: each path occupies one slot inside its
 *      category box, stacked in canonical order (by aaf_strategy color-dim)
 *   3. Smoothstep-interpolated ribbons between adjacent axes (14 samples),
 *      colored by aaf_strategy, opacity reflects brush pass-fraction
 *   4. Click-toggle selection_point per axis, AND-filtered across all axes
 *
 * The notebook iterates over raw award rows; our backend already aggregates
 * to the path grain. We replay the same slot-building logic over those rows.
 */
import { useEffect, useMemo, useRef } from 'react'
import embed, { type Result, type VisualizationSpec } from 'vega-embed'
import type { PathwayRow, PathwayAxis } from './usePathwayFlowQuery'
import { PATHWAY_AXES, N_BINS } from './usePathwayFlowQuery'

export type Selection = Partial<Record<PathwayAxis, Set<string>>>

interface Props {
  rows: PathwayRow[]
  /**
   * Active histogram brushes in (binStart, binEnd) inclusive form.
   * null entries mean no brush on that channel.
   */
  brushes: {
    open: [number, number] | null
    eval: [number, number] | null
    amount: [number, number] | null
  }
  selection: Selection
  onSelectionChange: (sel: Selection) => void
  width?: number
  height?: number
}

export function pathKey(row: PathwayRow): string {
  return PATHWAY_AXES.map((a) => row[a] ?? '').join('|')
}

const RIB_COLOR_DIM: PathwayAxis = 'aaf_strategy'
const BAR_GAP = 0.012
const BAR_HALF_W = 0.018
const N_SAMPLES = 14
// Two clicks on the same category within this window count as a double-click
// (detected React-side — see the signal listener for why vega's own dblclick
// can't fire here).
const DBLCLICK_MS = 350
// vega-lite `padding` (left 10 + right 25) sits outside the plotting `width`,
// so the rendered SVG is this much wider than the `width` we pass the spec.
// Subtract it up front so the SVG fits the container instead of overflowing.
const SPEC_PAD_X = 35

export default function PathwayFlowChart({
  rows, brushes, selection, onSelectionChange, width = 1000, height = 560,
}: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const embedResultRef = useRef<Result | null>(null)
  const selectionRef = useRef<Selection>(selection)
  const onSelectionChangeRef = useRef(onSelectionChange)
  // Last category clicked per axis, for React-side double-click detection.
  const lastClickRef = useRef<Record<string, { v: string; t: number }>>({})

  // Keep latest callback + selection accessible inside Vega signal handlers.
  selectionRef.current = selection
  onSelectionChangeRef.current = onSelectionChange

  // `width` is the total horizontal space available; the vega-lite plotting
  // area is that minus the spec's fixed horizontal padding.
  const plotWidth = Math.max(width - SPEC_PAD_X, 240)
  const spec = useMemo(() => buildSpec(rows, brushes, selection, plotWidth, height),
    [rows, brushes, selection, plotWidth, height])

  useEffect(() => {
    if (!containerRef.current) return
    let cancelled = false
    embed(containerRef.current, spec as VisualizationSpec, {
      actions: false,
      renderer: 'svg',
      tooltip: { theme: 'light' },
    }).then((result) => {
      if (cancelled) { result.finalize(); return }
      embedResultRef.current = result

      // Wire each per-axis selection signal back into React state.
      for (const axis of PATHWAY_AXES) {
        const signalName = `sel_pcat_${axis}_tuple`
        result.view.addSignalListener(signalName, (_n, value: unknown) => {
          const vals = extractAxisValues(axis, value)
          const next: Selection = { ...selectionRef.current }

          // Each click lands in a fresh embed (the spec re-renders on every
          // selection change), so vega never sees two clicks on one view and
          // its native dblclick-to-clear can't fire. Detect it here instead:
          // the same category clicked twice within DBLCLICK_MS resets the axis.
          const clicked = vals[0]
          const now = Date.now()
          const prev = lastClickRef.current[axis]
          const isDoubleClick =
            clicked !== undefined && prev?.v === clicked && now - prev.t < DBLCLICK_MS
          if (clicked !== undefined) lastClickRef.current[axis] = { v: clicked, t: now }

          if (vals.length === 0 || isDoubleClick) {
            // double-click a category (or a vega clear) → reset the dimension
            delete next[axis]
          } else {
            // Single click — `vals` is just the clicked category; toggle it
            // into the React-side set to support multi-select per axis.
            const cur = new Set(next[axis] ?? [])
            for (const v of vals) {
              if (cur.has(v)) cur.delete(v)
              else cur.add(v)
            }
            if (cur.size === 0) delete next[axis]
            else next[axis] = cur
          }
          onSelectionChangeRef.current(next)
        })
      }
    }).catch((err) => {
      console.error('vega-embed failed', err)
    })
    return () => {
      cancelled = true
      embedResultRef.current?.finalize()
      embedResultRef.current = null
    }
  }, [spec])

  if (rows.length === 0) {
    return (
      <div className="flex items-center justify-center w-full h-full text-muted-foreground text-sm">
        No paths match the current filters.
      </div>
    )
  }

  return <div ref={containerRef} style={{ width, height }} />
}

// ──────────────────────────────────────────────────────────────────────────
// Spec construction (TypeScript port of the notebook's chart cell)
// ──────────────────────────────────────────────────────────────────────────

interface BarRecord {
  axis_x: number
  axis_name: PathwayAxis
  category: string
  count: number
  y_top: number
  y_bot: number
  y_center: number
  bar_height: number
  selected: boolean
  [k: string]: string | number | boolean
}

interface RibbonRecord {
  ribbon_id: number
  path_id: number
  count: number
  pass_frac: number
  axis_x: number
  y_top: number
  y_bot: number
  far_or_nonfar: string
  aaf_strategy: string
  instrument: string
  competition: string
  set_aside_class: string
  pricing: string
  notice_class: string
}

function buildSpec(
  rows: PathwayRow[],
  brushes: Props['brushes'],
  selection: Selection,
  width: number,
  height: number,
) {
  if (rows.length === 0) {
    return { $schema: 'https://vega.github.io/schema/vega-lite/v5.json', data: { values: [] }, mark: 'point' } as const
  }

  // 1. Bar bounds frozen against ALL rows (count = sum award_count per category).
  const barBounds: Record<string, [number, number]> = {} // key: `${axisIdx}|${cat}`
  const catCountsFull: Record<PathwayAxis, Record<string, number>> = {} as never
  const catOrder: Record<PathwayAxis, string[]> = {} as never

  for (let ai = 0; ai < PATHWAY_AXES.length; ai++) {
    const axis = PATHWAY_AXES[ai]
    const counts = new Map<string, number>()
    for (const r of rows) {
      const v = String(r[axis] ?? '')
      counts.set(v, (counts.get(v) ?? 0) + r.award_count)
    }
    const ordered = Array.from(counts.entries()).sort((a, b) => b[1] - a[1])
    const total = ordered.reduce((s, [, c]) => s + c, 0) || 1
    const n = ordered.length
    const totalGap = BAR_GAP * Math.max(n - 1, 0)
    const usable = Math.max(1.0 - totalGap, 0.5)
    let cum = 0
    for (const [cat, ct] of ordered) {
      const h = (ct / total) * usable
      const yTop = 1.0 - cum
      const yBot = yTop - h
      barBounds[`${ai}|${cat}`] = [yTop, yBot]
      cum += h + BAR_GAP
    }
    catCountsFull[axis] = Object.fromEntries(ordered)
    catOrder[axis] = ordered.map(([v]) => v)
  }

  // 2. Per-path brush pass-fraction.
  const brushSpec = [
    ['days_open_bins', brushes.open] as const,
    ['days_eval_bins', brushes.eval] as const,
    ['log_amount_bins', brushes.amount] as const,
  ]
  const brushActive = brushSpec.some(([, b]) => b !== null)
  const passFrac = rows.map((r) => {
    if (!brushActive) return 1
    let frac = 1
    for (const [key, brush] of brushSpec) {
      if (!brush) continue
      const bins = r[key] as number[]
      const total = bins.reduce((s, n) => s + n, 0) || 1
      let inside = 0
      for (let i = brush[0]; i <= brush[1] && i < N_BINS; i++) inside += bins[i] ?? 0
      frac *= inside / total
    }
    return frac
  })

  // 3. Canonical path order: by color-dim (aaf_strategy) first, then by axis positions.
  const colorOrder = catOrder[RIB_COLOR_DIM]
  const colorPos = new Map(colorOrder.map((c, i) => [c, i]))
  const axisPosMaps = PATHWAY_AXES.map((axis) => new Map(catOrder[axis].map((c, i) => [c, i])))
  const sortedIdx = rows.map((_r, i) => i).sort((a, b) => {
    const ra = rows[a]; const rb = rows[b]
    const cA = colorPos.get(String(ra[RIB_COLOR_DIM])) ?? 99
    const cB = colorPos.get(String(rb[RIB_COLOR_DIM])) ?? 99
    if (cA !== cB) return cA - cB
    for (let ai = 0; ai < PATHWAY_AXES.length; ai++) {
      const pA = axisPosMaps[ai].get(String(ra[PATHWAY_AXES[ai]])) ?? 99
      const pB = axisPosMaps[ai].get(String(rb[PATHWAY_AXES[ai]])) ?? 99
      if (pA !== pB) return pA - pB
    }
    return 0
  })

  // 4. Per-path y-slot per node. Stack downward inside each category box.
  type Slot = [number, number]
  const nodeSlots = new Map<string, Slot>() // key: `${axisIdx}|${path_id}`
  for (let ai = 0; ai < PATHWAY_AXES.length; ai++) {
    const axis = PATHWAY_AXES[ai]
    const slotOffset = new Map<string, number>()
    for (let pid = 0; pid < sortedIdx.length; pid++) {
      const r = rows[sortedIdx[pid]]
      const cat = String(r[axis])
      const ec = r.award_count
      const [barTop, barBot] = barBounds[`${ai}|${cat}`] ?? [0.5, 0.5]
      const barH = barTop - barBot
      const fullTotal = catCountsFull[axis][cat] ?? ec ?? 1
      const h = (ec / fullTotal) * barH
      const off = slotOffset.get(cat) ?? 0
      const yt = barTop - off
      const yb = yt - h
      nodeSlots.set(`${ai}|${pid}`, [yt, yb])
      slotOffset.set(cat, off + h)
    }
  }

  // 5. Bar dataframe rows (one per category per axis).
  const barRecords: BarRecord[] = []
  for (let ai = 0; ai < PATHWAY_AXES.length; ai++) {
    const axis = PATHWAY_AXES[ai]
    const axisSel = selection[axis]
    const axisHasSel = !!axisSel && axisSel.size > 0
    for (const cat of catOrder[axis]) {
      const [yTop, yBot] = barBounds[`${ai}|${cat}`]
      const ct = catCountsFull[axis][cat] ?? 0
      barRecords.push({
        axis_x: ai,
        axis_name: axis,
        category: cat,
        count: ct,
        y_top: yTop,
        y_bot: yBot,
        y_center: (yTop + yBot) / 2,
        bar_height: yTop - yBot,
        // No selection on this axis → every bar reads as "active" (dark);
        // with a selection, only the chosen categories stay dark.
        selected: !axisHasSel || axisSel!.has(cat),
        [axis]: cat,
      })
    }
  }

  // Active per-axis category filters (AND across axes). Selecting recomputes
  // the spec, so filtering the ribbon records here is deterministic and
  // survives the re-embed — unlike a vega `vlSelectionTest` filter, whose
  // selection store is reset to empty every time the chart is re-embedded.
  const selEntries = (Object.entries(selection) as [PathwayAxis, Set<string>][])
    .filter(([, set]) => set && set.size > 0)
  const pathMatchesSel = (r: PathwayRow) =>
    selEntries.every(([axis, set]) => set.has(String(r[axis])))

  // 6. Ribbon records (smoothstep-interpolated path segments between axes).
  const ribbonRecords: RibbonRecord[] = []
  let ribbonId = 0
  for (let i = 0; i < PATHWAY_AXES.length - 1; i++) {
    const xSrc = i + BAR_HALF_W
    const xDst = (i + 1) - BAR_HALF_W
    for (let pid = 0; pid < sortedIdx.length; pid++) {
      const r = rows[sortedIdx[pid]]
      if (!pathMatchesSel(r)) continue
      const [srcTop, srcBot] = nodeSlots.get(`${i}|${pid}`)!
      const [dstTop, dstBot] = nodeSlots.get(`${i + 1}|${pid}`)!
      for (let s = 0; s < N_SAMPLES; s++) {
        const t = s / (N_SAMPLES - 1)
        const e = t * t * (3 - 2 * t)
        const xAt = xSrc + (xDst - xSrc) * t
        const ytAt = srcTop + (dstTop - srcTop) * e
        const ybAt = srcBot + (dstBot - srcBot) * e
        ribbonRecords.push({
          ribbon_id: ribbonId,
          path_id: pid,
          count: r.award_count,
          pass_frac: passFrac[sortedIdx[pid]],
          axis_x: xAt,
          y_top: ytAt,
          y_bot: ybAt,
          far_or_nonfar: String(r.far_or_nonfar),
          aaf_strategy: String(r.aaf_strategy),
          instrument: String(r.instrument),
          competition: String(r.competition),
          set_aside_class: String(r.set_aside_class),
          pricing: String(r.pricing),
          notice_class: String(r.notice_class),
        })
      }
      ribbonId += 1
    }
  }

  // 7. Axis headers (one row per axis).
  const headerRecords = PATHWAY_AXES.map((axis, i) => ({
    axis_x: i, y: 1.04, label: AXIS_LABELS[axis],
  }))

  // 8. Selections — one per axis (capture clicks; ribbon/bar visuals are
  // driven by the React selection above, not by these vega stores).
  const X_DOMAIN = [-0.35, PATHWAY_AXES.length - 1 + 0.35]
  const Y_DOMAIN = [-0.04, 1.06]

  // Build per-axis selection params with axis-scoped event filters.
  const params = PATHWAY_AXES.map((axis) => {
    const onFilter = axis === RIB_COLOR_DIM
      ? `click[event.item && event.item.datum && event.item.datum.${RIB_COLOR_DIM} && (!event.item.datum.axis_name || event.item.datum.axis_name === '${RIB_COLOR_DIM}')]`
      : `click[event.item && event.item.datum && event.item.datum.axis_name === '${axis}']`
    const clearFilter = axis === RIB_COLOR_DIM
      ? `dblclick[event.item && event.item.datum && event.item.datum.${RIB_COLOR_DIM} && (!event.item.datum.axis_name || event.item.datum.axis_name === '${RIB_COLOR_DIM}')]`
      : `dblclick[event.item && event.item.datum && event.item.datum.axis_name === '${axis}']`
    return {
      name: `sel_pcat_${axis}`,
      select: { type: 'point' as const, fields: [axis], toggle: 'true', on: onFilter, clear: clearFilter },
    }
  })

  // 9. Ribbon layer
  const ribbonOpacityEncoding = brushActive
    ? { field: 'pass_frac', type: 'quantitative' as const, scale: { domain: [0, 1], range: [0.05, 0.55] }, legend: null }
    : { value: 0.5 }

  const ribbonsLayer = {
    data: { values: ribbonRecords },
    mark: { type: 'area' as const, interpolate: 'linear' as const, tooltip: true },
    encoding: {
      x: { field: 'axis_x', type: 'quantitative' as const, scale: { domain: X_DOMAIN }, axis: null },
      y: { field: 'y_top', type: 'quantitative' as const, scale: { domain: Y_DOMAIN }, axis: null },
      y2: { field: 'y_bot' },
      detail: { field: 'ribbon_id', type: 'nominal' as const },
      opacity: ribbonOpacityEncoding,
      color: {
        field: RIB_COLOR_DIM,
        type: 'nominal' as const,
        scale: { domain: colorOrder, scheme: 'tableau20' },
        legend: null,
      },
      tooltip: [
        { field: 'count', type: 'quantitative' as const, title: 'awards' },
        { field: 'aaf_strategy', type: 'nominal' as const, title: 'AAF strategy' },
        { field: 'far_or_nonfar', type: 'nominal' as const, title: 'FAR / Non-FAR' },
        { field: 'instrument', type: 'nominal' as const },
        { field: 'pricing', type: 'nominal' as const },
      ],
    },
  }

  // 10. Bar layers (one per axis so each carries its own selection param)
  const barLayers = PATHWAY_AXES.map((axis, ai) => {
    const axisBars = barRecords.filter((b) => b.axis_x === ai)
    return {
      data: { values: axisBars },
      params: [params[ai]],
      transform: [
        { calculate: `datum.axis_x - ${BAR_HALF_W}`, as: 'x_lo' },
        { calculate: `datum.axis_x + ${BAR_HALF_W}`, as: 'x_hi' },
      ],
      mark: { type: 'rect' as const, cursor: 'pointer', tooltip: true },
      encoding: {
        x: { field: 'x_lo', type: 'quantitative' as const, scale: { domain: X_DOMAIN }, axis: null },
        x2: { field: 'x_hi' },
        y: { field: 'y_top', type: 'quantitative' as const, scale: { domain: Y_DOMAIN }, axis: null },
        y2: { field: 'y_bot' },
        fill: {
          condition: { test: 'datum.selected', value: '#4b5563' },
          value: '#cbd5e1',
        },
        tooltip: [
          { field: axis, type: 'nominal' as const, title: axis },
          { field: 'count', type: 'quantitative' as const, title: 'awards' },
        ],
      },
    }
  })

  // 11. Label layers (one per axis; only show labels where the bar is tall enough)
  const labelLayers = PATHWAY_AXES.map((_axis, ai) => {
    const axisBars = barRecords.filter((b) => b.axis_x === ai && b.bar_height > 0.02)
    return {
      data: { values: axisBars },
      mark: { type: 'text' as const, align: 'right' as const, baseline: 'middle' as const,
              fontSize: 10, fontWeight: 500, dx: -5 },
      encoding: {
        x: { field: 'axis_x', type: 'quantitative' as const, scale: { domain: X_DOMAIN }, axis: null },
        y: { field: 'y_center', type: 'quantitative' as const, scale: { domain: Y_DOMAIN }, axis: null },
        text: { field: 'category', type: 'nominal' as const },
        color: {
          condition: { test: 'datum.selected', value: '#1e293b' },
          value: '#64748b',
        },
      },
    }
  })

  // 12. Axis headers
  const headerLayer = {
    data: { values: headerRecords },
    mark: { type: 'text' as const, fontSize: 12, fontWeight: 'bold' as const, color: '#1e293b' },
    encoding: {
      x: { field: 'axis_x', type: 'quantitative' as const, scale: { domain: X_DOMAIN }, axis: null },
      y: { field: 'y', type: 'quantitative' as const, scale: { domain: Y_DOMAIN }, axis: null },
      text: { field: 'label', type: 'nominal' as const },
    },
  }

  return {
    $schema: 'https://vega.github.io/schema/vega-lite/v5.json',
    width,
    height,
    padding: { left: 10, right: 25, top: 10, bottom: 10 },
    layer: [ribbonsLayer, ...barLayers, ...labelLayers, headerLayer],
    resolve: { scale: { x: 'shared' as const, y: 'shared' as const } },
    view: { stroke: null },
    config: { view: { stroke: null } },
  }
}

const AXIS_LABELS: Record<PathwayAxis, string> = {
  far_or_nonfar: 'FAR vs Non-FAR',
  aaf_strategy: 'AAF Strategy',
  instrument: 'Instrument',
  competition: 'Competition',
  set_aside_class: 'Set-Aside',
  pricing: 'Pricing',
  notice_class: 'Notice Type',
}

/**
 * Decode whatever Vega writes to `sel_pcat_<axis>_tuple` into the list of
 * selected category values for that axis. Empty/null → []; single point →
 * `{values: [v]}` style; multi-select tuples come as `{or: [...]}`.
 */
function extractAxisValues(axis: PathwayAxis, raw: unknown): string[] {
  if (raw == null) return []
  // Single point: { _vgsid_, fields: [axis], values: [v] }
  if (typeof raw === 'object' && raw && 'values' in raw && Array.isArray((raw as { values: unknown }).values)) {
    return (raw as { values: unknown[] }).values.map((v) => String(v))
  }
  // Multi-select: { or: [ { axis: v }, ... ] }
  if (typeof raw === 'object' && raw && 'or' in raw) {
    return (raw as { or: Record<string, unknown>[] }).or
      .map((d) => String(d[axis] ?? ''))
      .filter(Boolean)
  }
  return []
}
